import React from 'react'
import {
  createChart,
  type IChartApi,
  type CandlestickSeriesPartialOptions,
  type CandlestickSeriesOptions,
  type CandlestickData,
  type UTCTimestamp,
  type LineData,
} from 'lightweight-charts'
import { formatDateTime } from '@/shared/utils/date'
import { useCandles } from '../hooks/useCandles'
import type { TF } from '../types'
import { emaFromCandles } from '../utils/indicators'
import {
  normalizeCandles,
  normalizeVolumes,
  detectPriceFormat as detectPF,
  devValidateCandles,
  filterOutliers,
} from '../utils/normalize'

export type OHLCVChartProps = {
  symbol: string
  timeframe: TF
  limit?: number
  className?: string
  onReady?: (chart: IChartApi) => void
  seriesOptions?: CandlestickSeriesPartialOptions | CandlestickSeriesOptions
  showInternalControls?: boolean
  ema12?: boolean
  ema26?: boolean
  ema200?: boolean
  volume?: boolean
  footerSlot?: React.ReactNode
  footerHeight?: number
  // Marcadores opcionais (Lightweight Charts markers na sÇ¸rie de candles)
  markers?: Array<{
    time: number | UTCTimestamp
    position?: 'aboveBar' | 'belowBar' | 'inBar'
    color?: string
    shape?: any
    text?: string
  }>
  // Timezone (IANA) usado para formatar eixo de tempo e crosshair. Default: Etc/UTC.
  timezone?: string
  // Quando true, bloqueia interaÇõÇœo de escala vertical (mouse, pinch, arrasto na rÇ¸gua de preÇõo).
  // Usado no desktop para evitar zoom vertical inesperado em alguns navegadores/dispositivos.
  disableVerticalScale?: boolean
}

export default function OHLCVChart({
  symbol,
  timeframe,
  limit = 500,
  className,
  onReady,
  seriesOptions,
  showInternalControls = true,
  ema12,
  ema26,
  ema200,
  volume = true,
  footerSlot,
  footerHeight = 140,
  markers,
  timezone,
  disableVerticalScale = false,
}: OHLCVChartProps) {
  const containerRef = React.useRef<HTMLDivElement | null>(null)
  const chartRef = React.useRef<IChartApi | null>(null)
  const seriesRef = React.useRef<ReturnType<IChartApi['addCandlestickSeries']> | null>(null)
  const ema12Ref = React.useRef<ReturnType<IChartApi['addLineSeries']> | null>(null)
  const ema26Ref = React.useRef<ReturnType<IChartApi['addLineSeries']> | null>(null)
  const ema200Ref = React.useRef<ReturnType<IChartApi['addLineSeries']> | null>(null)
  const volRef = React.useRef<ReturnType<IChartApi['addHistogramSeries']> | null>(null)
  const [showEma12, setShowEma12] = React.useState(true)
  const [showEma26, setShowEma26] = React.useState(true)
  const [showEma200, setShowEma200] = React.useState(true)

  // MantÇ¸m o timezone efetivo (SSOT com useUserTimezone).
  const timezoneRef = React.useRef<string>('Etc/UTC')
  React.useEffect(() => {
    const tz = (timezone || '').trim() || 'Etc/UTC'
    timezoneRef.current = tz
  }, [timezone])

  const onReadyRef = React.useRef<typeof onReady>(onReady)
  React.useEffect(() => {
    onReadyRef.current = onReady
  }, [onReady])

  const { data, loading, error } = useCandles(symbol, timeframe, limit)

  // inicializa o chart apenas uma vez
  React.useEffect(() => {
    const lockY = !!disableVerticalScale
    const el = containerRef.current
    if (!el) return
    if (chartRef.current) return

    const chart = createChart(el, {
      layout: { background: { color: 'transparent' }, textColor: 'rgba(255,255,255,0.8)' },
      rightPriceScale: { borderColor: 'rgba(120,120,120,0.4)' },
      timeScale: {
        borderColor: 'rgba(120,120,120,0.4)',
        rightOffset: rightOffsetBars(timeframe),
        barSpacing: 6,
        rightBarStaysOnScroll: false,
        timeVisible: true,
        secondsVisible: false,
      } as any,
      grid: { vertLines: { color: 'rgba(120,120,120,0.1)' }, horzLines: { color: 'rgba(120,120,120,0.1)' } },
      crosshair: { mode: 0 },
      // pan com arrasto; wheel desativado para evitar zoom involuntÇ­rio
      // quando disableVerticalScale=true, bloqueia também arrasto vertical no gráfico
      handleScroll: {
        pressedMouseMove: true,
        mouseWheel: false,
        horzTouchDrag: true,
        vertTouchDrag: !lockY,
      } as any,
      // pinch para zoom interno; reset em double-click nos eixos
      // quando disableVerticalScale=true, pinch é desativado e a régua de preço não responde a arrasto
      handleScale: {
        mouseWheel: lockY,
        pinch: !lockY,
        axisPressedMouseMove: { time: true, price: true },
        axisDoubleClickReset: { time: true, price: true },
      } as any,
      autoSize: true,
      localization: {
        locale: 'pt-BR',
        // Garante que o horÇ­rio exibido respeite o timezone configurado para o usuÇ­rio.
        timeFormatter: (time: number | { timestamp?: number; year?: number; month?: number; day?: number }) => {
          try {
            let ts: number | null = null

            if (typeof time === 'number') {
              ts = time
            } else if (time && typeof time.timestamp === 'number') {
              ts = time.timestamp
            } else if (time && typeof time.year === 'number' && typeof time.month === 'number' && typeof time.day === 'number') {
              const d = new Date(Date.UTC(time.year, (time.month || 1) - 1, time.day || 1, 0, 0, 0))
              ts = Math.floor(d.getTime() / 1000)
            } else {
              ts = Number(time as any)
            }

            if (!Number.isFinite(ts)) return String(time as any)

            const iso = new Date(ts * 1000).toISOString()
            return formatDateTime(iso, {
              timeZone: timezoneRef.current || 'Etc/UTC',
              locale: 'pt-BR',
              includeSeconds: false,
            })
          } catch {
            return String(time as any)
          }
        },
      } as any,
    })

    const series = chart.addCandlestickSeries({
      upColor: '#22c55e',
      downColor: '#ef4444',
      wickUpColor: '#22c55e',
      wickDownColor: '#ef4444',
      borderVisible: false,
      ...seriesOptions,
    })
    const e12 = chart.addLineSeries({
      color: '#FFD600',
      lineWidth: 2,
      priceLineVisible: false,
      visible: ema12 ?? showEma12,
    })
    const e26 = chart.addLineSeries({
      color: '#00BCD4',
      lineWidth: 2,
      priceLineVisible: false,
      visible: ema26 ?? showEma26,
    })
    const e200 = chart.addLineSeries({
      color: '#8E44AD',
      lineWidth: 2,
      priceLineVisible: false,
      visible: ema200 ?? showEma200,
    })
    const vol = chart.addHistogramSeries({
      color: '#334155',
      priceLineVisible: false,
      base: 0,
      priceFormat: { type: 'volume' } as any,
      visible: !!volume,
      priceScaleId: 'vol' as any,
    })
    try {
      chart.priceScale('vol' as any).applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } })
    } catch {}

    chartRef.current = chart
    seriesRef.current = series
    ema12Ref.current = e12
    ema26Ref.current = e26
    ema200Ref.current = e200
    volRef.current = vol

    // aplica dados iniciais se jÇ  existirem
    try {
      const initial = normalizeCandles(data as any, timeframe)
      if (initial && initial.length > 0) {
        series.setData(initial)
        ;(series as any).__lastData = initial
        const pf = detectPF(initial)
        if (pf) {
          series.applyOptions({
            priceFormat: { type: 'price', precision: pf.precision, minMove: pf.minMove } as any,
          })
        }
        try {
          devValidateCandles(timeframe, initial as any)
        } catch {}
      }
    } catch {}

    if (typeof onReadyRef.current === 'function') {
      try {
        onReadyRef.current(chart)
      } catch {}
    }

    // resize responsivo
    const handleResize = () => {
      try {
        chart.applyOptions({ autoSize: true } as any)
      } catch {}
    }
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      try {
        chart.remove()
      } catch {}
      chartRef.current = null
      seriesRef.current = null
      ema12Ref.current = null
      ema26Ref.current = null
      ema200Ref.current = null
      volRef.current = null
    }
  }, [symbol, timeframe, disableVerticalScale])

  // ajusta offset quando muda TF
  React.useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    try {
      chart.timeScale().applyOptions({ rightOffset: rightOffsetBars(timeframe) } as any)
    } catch {}
  }, [timeframe])

  // atualiza candles, EMAs e markers
  React.useEffect(() => {
    const chart = chartRef.current
    const series = seriesRef.current
    if (!chart || !series) return
    const mapped = normalizeCandles(data as any, timeframe)
    const filtered = filterOutliers(mapped, timeframe)
    try {
      series.setData(filtered)
      ;(series as any).__lastData = filtered
    } catch {}
    try {
      const pf = detectPF(filtered)
      if (pf) {
        series.applyOptions({
          priceFormat: { type: 'price', precision: pf.precision, minMove: pf.minMove } as any,
        })
      }
    } catch {}
    try {
      devValidateCandles(timeframe, filtered as any)
    } catch {}
    // EMAs sobre o mesmo conjunto filtrado
    try {
      const e12 = emaFromCandles(filtered as any, 12)
      const e26 = emaFromCandles(filtered as any, 26)
      const e200 = emaFromCandles(filtered as any, 200)
      if (ema12Ref.current) ema12Ref.current.setData(e12 as LineData[])
      if (ema26Ref.current) ema26Ref.current.setData(e26 as LineData[])
      if (ema200Ref.current) ema200Ref.current.setData(e200 as LineData[])
    } catch {}

    if (!markers || markers.length === 0) return
    try {
      series.setMarkers(markers as any)
    } catch {}
  }, [data, timeframe, markers])

  // volumes
  React.useEffect(() => {
    const chart = chartRef.current
    const vol = volRef.current
    if (!chart || !vol) return
    const volumes = normalizeVolumes((data as any) || [], timeframe)
    try {
      vol.setData(volumes as any)
    } catch {}
    try {
      chart.applyOptions({
        layout: { background: { color: 'transparent' }, textColor: 'rgba(255,255,255,0.8)' },
      })
    } catch {}
  }, [data, timeframe])

  // visibilidade das EMAs (props + toggles internos)
  React.useEffect(() => {
    const e12 = ema12Ref.current
    const e26 = ema26Ref.current
    const e200 = ema200Ref.current
    if (!e12 || !e26 || !e200) return
    try {
      e12.applyOptions({ visible: ema12 ?? showEma12 })
      e26.applyOptions({ visible: ema26 ?? showEma26 })
      e200.applyOptions({ visible: ema200 ?? showEma200 })
    } catch {}
  }, [ema12, ema26, ema200, showEma12, showEma26, showEma200])

  const renderFooter = () => {
    if (!footerSlot) return null
    return (
      <div className="border-t border-border" style={{ height: footerHeight }}>
        {footerSlot}
      </div>
    )
  }

  const rootClassName = ['h-full w-full', className].filter(Boolean).join(' ')

  return (
    <div className={rootClassName}>
      {error && (
        <div className="mb-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive-foreground">
          {String(error)}
        </div>
      )}
      <div className="flex h-full flex-col">
        {showInternalControls && (
          <div className="mb-2 flex items-center justify-between text-xs text-foreground/70">
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  className="accent-emerald-400"
                  checked={showEma12}
                  onChange={(e) => setShowEma12(e.target.checked)}
                />
                <span style={{ color: '#FFD600' }}>EMA 12</span>
              </label>
              <label className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  className="accent-cyan-400"
                  checked={showEma26}
                  onChange={(e) => setShowEma26(e.target.checked)}
                />
                <span style={{ color: '#00BCD4' }}>EMA 26</span>
              </label>
              <label className="inline-flex items-center gap-1">
                <input
                  type="checkbox"
                  className="accent-violet-500"
                  checked={showEma200}
                  onChange={(e) => setShowEma200(e.target.checked)}
                />
                <span style={{ color: '#8E44AD' }}>EMA 200</span>
              </label>
            </div>
          </div>
        )}
        <div
          ref={containerRef}
          className="w-full touch-none overscroll-contain select-none"
          style={{ height: footerSlot ? `calc(100% - ${footerHeight}px)` : '100%' }}
        />
        {renderFooter()}
      </div>
    </div>
  )
}

function preferredVisibleCount(tf: TF): number {
  switch (tf) {
    case '5': return 150
    case '15': return 170
    case '30': return 190
    case '60': return 200
    case '240': return 200
    case '720': return 180
    case 'D': return 180
    case 'W': return 160
    case 'M': return 120
    default: return 220
  }
}

function tfToSeconds(tf: TF): number | null {
  switch (tf) {
    case '5': return 300
    case '15': return 900
    case '30': return 1800
    case '60': return 3600
    case '240': return 14400
    case '720': return 43200
    case 'D': return 86400
    case 'W': return null
    case 'M': return null
    default: return null
  }
}

function rightOffsetBars(tf: TF): number {
  switch (tf) {
    case 'W': return 3
    case 'M': return 1
    default: return 12
  }
}
